import React, { useEffect, useRef, useState } from 'react';
import { getProject, saveStatutory, updateProject } from '../data/projects';
import {
  buildStatutory, statPropKey, propWant, groupCounts, collectGaps, updateField,
  toggleGroupOpen, setAllOpen, fetchApplicablePlans, fetchDafMeyda, fetchParcelLandUse, CONFIDENCE, FIELD_STATUS, STAT_VERSION, DM_SCHEMA_V, canonicalSourceName,
} from '../data/statutory';
import { propertySignature } from '../data/identification';
import { uploadFile } from '../data/upload';
import Roadmap, { reachedStageFor } from '../components/Roadmap';
import AgentActivity from '../components/AgentActivity';
import DocumentArchive from '../components/DocumentArchive';
import ConfirmDialog from '../components/ConfirmDialog';
import NoteDialog from '../components/NoteDialog';
import './StatutoryAnalysis.css';

// Stage 03 — "ניתוח מצב סטטוטורי". Turns the collected sources (chunk 2) into a
// reviewed data layer: subject lists of extracted values the user approves /
// edits / overrides / completes. No rights calc, no existing-state build.
const ACTIVE_STAGE = 2;

function Pill({ tone, children }) {
  return <span className={`sa-pill sa-pill-${tone}`}>{children}</span>;
}

function yearOf(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : String(dt.getFullYear());
}

function fmtDMY(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}

// The spec's three-tier source breadcrumb (Database › Dataset · page/section) —
// shown as the source link's tooltip so the precise origin is always inspectable.
function sourceBreadcrumb(field) {
  const parts = [];
  if (field.database?.name) parts.push(field.database.name);
  if (field.dataset?.name) parts.push(field.dataset.name);
  let s = parts.join(' › ');
  if (field.sourceRef?.section && !s.includes(field.sourceRef.section)) s += ` · ${field.sourceRef.section}`;
  if (field.sourceRef?.page) s += ` · עמ׳ ${field.sourceRef.page}`;
  return s || field.source?.label || '';
}


// Conflict-resolution dialog (§14): compares the candidate values from the
// different plans with room for full names / dates / links, and lets the user
// pick the governing source. Picking applies immediately; the dialog stays open
// so the choice can be compared/changed.
function ConflictDialog({ field, onPick, onClose, docs = [], onLinkDoc, onManual }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [manualVal, setManualVal] = useState('');
  const candidates = field.candidates || [];
  const isConflict = Boolean(field.conflict && !field.value);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="sa-dlg-backdrop" onMouseDown={onClose}>
      <div ref={ref} tabIndex={-1} className="sa-dlg sa-cdlg" role="dialog" aria-modal="true" aria-label={`${isConflict ? 'ערכים סותרים' : 'החלפת מקור'} — ${field.name}`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="sa-dlg-head">
          <h3>
            {isConflict ? <i className="fi fi-rr-triangle-warning sa-cdlg-warn" /> : <i className="fi fi-rr-refresh" />}
            {' '}{field.name} — {isConflict ? 'ערכים סותרים' : 'החלפת מקור'}
          </h3>
          <button type="button" className="sa-dlg-x" onClick={onClose} aria-label="סגור"><i className="fi fi-rr-cross-small" /></button>
        </header>
        <p className="sa-cdlg-sub">
          {field.conflictKind === 'zone'
            ? <>הערכים שייכים לאזורי משנה שונים בתב״ע. בחרו את האזור התואם לייעוד הנכס{field.parcelZone ? ` (${field.parcelZone})` : ''}.</>
            : candidates.length > 1
              ? <>נמצאו {candidates.length} ערכים ממקורות שונים. בחרו את המקור הקובע.</>
              : <>בחירת מקור מהרשימה מושכת את הערך ממנו; קישור מסמך או הזנה ידנית מתועדים בסטטוס הנתון.</>}
        </p>
        {field.systemRecommendation?.reasonHe && (
          <p className="sa-cdlg-reco">
            <i className="fi fi-rr-bulb" /> המלצת המערכת: <strong>{field.systemRecommendation.value}{field.unit ? ` ${field.unit}` : ''}</strong> — {field.systemRecommendation.reasonHe}
          </p>
        )}
        {candidates.length > 0 && (
          <ul className="sa-cdlg-list">
            {candidates.map((c, i) => {
              const on = c.value === field.value;
              const recommended = field.systemRecommendation && c.value === field.systemRecommendation.value;
              return (
                <li key={i} className={`sa-cdlg-item ${on ? 'is-on' : ''}`}>
                  <button type="button" className="sa-cdlg-pick" onClick={() => onPick(c)}>
                    <span className="sa-cand-radio" />
                    <span className="sa-cdlg-val">{c.value}{c.unit ? ` ${c.unit}` : ''}</span>
                    <span className="sa-cdlg-plan">
                      <span className="sa-cdlg-num">
                        {c.planNumber || c.source?.label || 'מקור'}
                        {recommended && candidates.length > 1 && <span className="sa-cand-recent">מומלץ</span>}
                      </span>
                      {c.zoneName && <span className="sa-cdlg-zone">{c.zoneName}</span>}
                      <span className="sa-cdlg-name">
                        {[
                          c.source?.database?.name && c.source?.dataset?.name
                            ? `${c.source.database.name} › ${c.source.dataset.name}`
                            : c.planName,
                          c.planDate ? yearOf(c.planDate) : null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                      {c.disqualifier && <span className="sa-cdlg-disq"><i className="fi fi-rr-info" /> {c.disqualifier}</span>}
                    </span>
                  </button>
                  {c.source?.link && (
                    <a className="sa-cdlg-open" href={c.source.link} target="_blank" rel="noreferrer" title="פתח את המקור"><i className="fi fi-rr-arrow-up-right-from-square" /> מקור</a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {candidates.length === 0 && (
          <p className="sa-cdlg-none">לא נמצאו מקורות חלופיים אוטומטיים לנתון זה.</p>
        )}
        {(onLinkDoc || onManual) && (
          <div className="sa-cdlg-alt">
            {onLinkDoc && docs.length > 0 && (
              <label className="sa-cdlg-alt-row">
                <span className="sa-cdlg-alt-label"><i className="fi fi-rr-document" /> קשר מסמך מהארכיון</span>
                <select defaultValue="" onChange={(e) => { const d = docs[Number(e.target.value)]; if (d) { onLinkDoc(d); onClose(); } }}>
                  <option value="" disabled>בחרו מסמך…</option>
                  {docs.map((d, i) => <option key={i} value={i}>{d.name}</option>)}
                </select>
              </label>
            )}
            {onManual && (
              <label className="sa-cdlg-alt-row">
                <span className="sa-cdlg-alt-label"><i className="fi fi-rr-pencil" /> הזנה ידנית</span>
                <span className="sa-cdlg-manual">
                  <input
                    value={manualVal}
                    onChange={(e) => setManualVal(e.target.value)}
                    placeholder={field.value && field.source ? 'ערך חדש (יחליף את ערך המקור)' : 'ערך ידני (הזנת משתמש)'}
                    onKeyDown={(e) => { if (e.key === 'Enter' && manualVal.trim()) { onManual(manualVal.trim()); onClose(); } }}
                  />
                  <button type="button" className="np-btn np-btn-ghost" disabled={!manualVal.trim()} onClick={() => { onManual(manualVal.trim()); onClose(); }}>שמור</button>
                </span>
              </label>
            )}
          </div>
        )}
        <footer className="sa-dlg-foot">
          <span className="sa-dlg-spacer" />
          <button type="button" className="np-btn np-btn-primary" onClick={onClose}>סיום</button>
        </footer>
      </div>
    </div>
  );
}

// A field row. The value is directly editable — click it to edit inline; saving
// a change asks how to keep it (correct vs override) via a dialog. Secondary
// actions are inline icons, not a dropdown.
// Tiny inline plot of the parcel polygon (graphic-log §5.1) — the geometry
// itself, not a verbal description. Coordinates are EPSG:2039 metres.
function PolygonThumb({ rings }) {
  const r = (rings && rings[0]) || [];
  if (r.length < 3) return null;
  let minx = Infinity; let miny = Infinity; let maxx = -Infinity; let maxy = -Infinity;
  for (const [x, y] of r) {
    if (x < minx) minx = x; if (x > maxx) maxx = x;
    if (y < miny) miny = y; if (y > maxy) maxy = y;
  }
  const w = maxx - minx || 1; const h = maxy - miny || 1;
  const S = 96; const pad = 6;
  const k = Math.min((S - pad * 2) / w, (S - pad * 2) / h);
  const pts = r.map(([x, y]) => `${(pad + (x - minx) * k).toFixed(1)},${(S - pad - (y - miny) * k).toFixed(1)}`).join(' ');
  const coords = r.map(([x, y]) => `${Math.round(x)},${Math.round(y)}`).join(' · ');
  return (
    <svg className="sa-poly" viewBox={`0 0 ${S} ${S}`} width="86" height="86" role="img" aria-label="פוליגון מגרש">
      <title>{`קואורדינטות (ITM): ${coords}`}</title>
      <polygon points={pts} />
    </svg>
  );
}

function FieldRow({ field, onAction, onEditValue, onNote, onOpenConflict }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // An unresolved conflict reads as "needs a decision", not a generic check.
  const st = (field.conflict && !field.value)
    ? { label: 'דורש הכרעה', tone: 'bad' }
    : (FIELD_STATUS[field.status] || FIELD_STATUS.check);
  const conf = CONFIDENCE[field.confidence] || CONFIDENCE.missing;
  const dim = field.status === 'not_in_scope';

  const startEdit = () => { setDraft(field.value || ''); setEditing(true); };
  const commit = () => { setEditing(false); onEditValue(field, draft); };

  return (
    <div className={`sa-tr ${dim ? 'is-dim' : ''}`} role="row">
      <span className="sa-td sa-c-name" data-label="נתון">{field.name}</span>
      <span className="sa-td sa-c-value" data-label="ערך">
        {editing ? (
          <span className="sa-edit">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
              placeholder="הקלידו ערך"
            />
            <button type="button" className="sa-edit-ok" onClick={commit} aria-label="שמור"><i className="fi fi-rr-check" /></button>
            <button type="button" className="sa-edit-x" onClick={() => setEditing(false)} aria-label="בטל"><i className="fi fi-rr-cross-small" /></button>
          </span>
        ) : field.conflict && field.candidates ? (
          // Compact trigger — the candidate comparison happens in a dialog.
          field.value ? (
            <span className="sa-resolved">
              <span className="sa-value">{field.value}</span>
              {field.unit && <span className="sa-unit">{field.unit}</span>}
              {(field.valueKind === 'geometric_rule' || field.valueKind === 'conditional_cap') && (
                <span className={`sa-kind sa-kind-${field.valueKind}`} title={field.valueKind === 'conditional_cap' ? 'תקרה החלה רק בהתקיים תנאי — לא ערך קבוע' : 'הערך הוא כלל חישוב, לא מספר קבוע'}>
                  {field.valueKind === 'conditional_cap' ? 'כלל מותנה' : 'כלל חישוב'}
                </span>
              )}
              {field.extra && <span className="sa-extra">{field.extra}</span>}
              <button type="button" className="sa-conflict-chip is-resolved" onClick={() => onOpenConflict(field)} title="שינוי המקור הקובע">
                <i className="fi fi-rr-layers" /> {field.candidates.length} {field.conflictKind === 'zone' ? 'אזורים' : 'מקורות'}
              </button>
            </span>
          ) : (
            <button type="button" className="sa-conflict-chip is-open" onClick={() => onOpenConflict(field)}>
              <i className="fi fi-rr-triangle-warning" /> {field.conflictKind === 'zone' ? `בחרו אזור משנה (${field.candidates.length})` : `${field.candidates.length} ערכים סותרים — הכריעו`}
            </button>
          )
        ) : (
          <button type="button" className={`sa-value-btn ${field.value ? '' : 'is-empty'}`} onClick={startEdit} title="לחצו לעריכת הערך">
            {field.value ? (
              <>
                <span className="sa-value">{field.value}</span>
                {field.unit && <span className="sa-unit">{field.unit}</span>}
                {(field.valueKind === 'geometric_rule' || field.valueKind === 'conditional_cap') && (
                  <span className={`sa-kind sa-kind-${field.valueKind}`} title={field.valueKind === 'conditional_cap' ? 'תקרה החלה רק בהתקיים תנאי — לא ערך קבוע' : 'הערך הוא כלל חישוב, לא מספר קבוע'}>
                    {field.valueKind === 'conditional_cap' ? 'כלל מותנה' : 'כלל חישוב'}
                  </span>
                )}
                {field.extra && <span className="sa-extra">{field.extra}</span>}
                {field.originalValue && field.originalValue !== field.value && (
                  <span className="sa-orig" title="הערך המקורי שחולץ מהמקור">מקורי: <s>{field.originalValue}</s></span>
                )}
                <i className="fi fi-rr-pencil sa-value-pen" />
              </>
            ) : <span className="sa-add-val"><i className="fi fi-rr-plus-small" /> הוסיפו ערך</span>}
          </button>
        )}
        {field.note && !editing && <span className="sa-note" title={field.note}><i className="fi fi-rr-comment" /> {field.note}</span>}
        {field.polygon && !editing && <PolygonThumb rings={field.polygon} />}
      </span>
      <span className="sa-td sa-c-unit" data-label="יחידה">{field.unit || '—'}</span>
      <span className="sa-td sa-c-source" data-label="מקור">
        {field.source ? (
          field.source.link
            ? <a className="sa-src" href={field.source.link} target="_blank" rel="noreferrer" title={sourceBreadcrumb(field)}>{field.source.label} <i className="fi fi-rr-arrow-up-right-from-square" /></a>
            : <span className="sa-src sa-src-plain" title={sourceBreadcrumb(field)}>{field.source.label}</span>
        ) : (field.expectedSource
          ? <span className="sa-missing-val" title={`המקור הצפוי לנתון זה: ${field.expectedSource}`}>ייאסף מ: {field.expectedSource}</span>
          : <span className="sa-missing-val">—</span>)}
      </span>
      <span className="sa-td sa-c-conf" data-label="ודאות"><Pill tone={conf.tone}>{conf.label}</Pill></span>
      <span className="sa-td sa-c-status" data-label="סטטוס">
        <span title={field.lastUpdated ? `עדכון אחרון: ${new Date(field.lastUpdated).toLocaleString('he-IL')}` : undefined}>
          <Pill tone={st.tone}>{st.label}</Pill>
        </span>
      </span>
      <span className="sa-td sa-c-act">
        {field.status === 'check' && !editing && field.value && (
          <button type="button" className="sa-ib sa-ib-approve" data-tip="אשר" onClick={() => onAction(field.id, 'approve')}>
            <i className="fi fi-rr-check" />
          </button>
        )}
        {field.status === 'approved' && (
          <span className="sa-ib sa-ib-approved" data-tip="אושר">
            <i className="fi fi-rr-check" />
          </span>
        )}
        {!editing && field.status !== 'not_in_scope' && onOpenConflict && (
          <button type="button" className="sa-ib" data-tip="החלף מקור" onClick={() => onOpenConflict(field)}>
            <i className="fi fi-rr-refresh" />
          </button>
        )}
        <button type="button" className={`sa-ib ${field.note ? 'is-on' : ''}`} data-tip="הערה" onClick={() => onNote(field)}>
          <i className="fi fi-rr-comment" />
        </button>
        {field.status === 'not_in_scope' ? (
          <button type="button" className="sa-ib" data-tip="החזרה לסקופ" onClick={() => onAction(field.id, 'restore')}>
            <i className="fi fi-rr-undo" />
          </button>
        ) : field.value && (
          <button type="button" className="sa-ib sa-ib-danger" data-tip="הסרה מסקופ" onClick={() => onAction(field.id, 'scope')}>
            <i className="fi fi-rr-ban" />
          </button>
        )}
      </span>
    </div>
  );
}

// Applicable-plans row (§9.5): number, name, type, primary role, relevance,
// status, source + review actions. Plans aren't inline-value-edited.
function PlanRow({ field, onAction, onNote }) {
  const dim = field.status === 'not_in_scope';
  const rel = field.relevance || { label: '—', tone: 'neutral' };
  return (
    <div className={`sa-plan ${dim ? 'is-dim' : ''}`} role="row">
      <span className="sa-plan-id" data-label="תכנית">
        <span className="sa-plan-num">{field.name}</span>
        <span className="sa-plan-name" title={field.value}>{field.value}</span>
        {(field.depositDate || field.onlineNumber) && (
          <span className="sa-plan-meta">
            {field.depositDate && `הופקדה ${fmtDMY(field.depositDate)}`}
            {field.depositDate && field.onlineNumber && ' · '}
            {field.onlineNumber && `מקוון ${field.onlineNumber}`}
          </span>
        )}
      </span>
      <span className="sa-plan-role" data-label="סוג ותפקיד">
        <span className="sa-plan-typetag">{field.planType}</span>
        <span className="sa-plan-roletag">{field.role}</span>
      </span>
      <span className="sa-plan-rel" data-label="רלוונטיות">
        <Pill tone={rel.tone}>{rel.label}</Pill>
        {field.needsFurther && <span className="sa-plan-further" title="נדרש ניתוח המשך בשלב הבא"><i className="fi fi-rr-arrow-progress" /> ניתוח המשך</span>}
      </span>
      <span className="sa-plan-status" data-label="סטטוס"><Pill tone={field.statusTone || 'neutral'}>{field.statusLabel}</Pill></span>
      <span className="sa-plan-src" data-label="מקור">
        {field.source?.link
          ? <a className="sa-src" href={field.source.link} target="_blank" rel="noreferrer">פתח תכנית <i className="fi fi-rr-arrow-up-right-from-square" /></a>
          : <span className="sa-src sa-src-plain">{field.source?.label || '—'}</span>}
      </span>
      <span className="sa-plan-act">
        {field.status === 'check' && (
          <button type="button" className="sa-ib sa-ib-approve" data-tip="אשר" onClick={() => onAction(field.id, 'approve')}><i className="fi fi-rr-check" /></button>
        )}
        {field.status === 'approved' && (
          <span className="sa-ib sa-ib-approved" data-tip="אושר"><i className="fi fi-rr-check" /></span>
        )}
        <button type="button" className={`sa-ib ${field.note ? 'is-on' : ''}`} data-tip="הערה" onClick={() => onNote(field)}><i className="fi fi-rr-comment" /></button>
        {field.status === 'not_in_scope' ? (
          <button type="button" className="sa-ib" data-tip="החזרה לסקופ" onClick={() => onAction(field.id, 'restore')}><i className="fi fi-rr-undo" /></button>
        ) : (
          <button type="button" className="sa-ib sa-ib-danger" data-tip="הסרה מסקופ" onClick={() => onAction(field.id, 'scope')}><i className="fi fi-rr-ban" /></button>
        )}
      </span>
    </div>
  );
}

// §2 — "המשתמש יכול להוסיף נתון חדש": an inline add-row at the bottom of each
// subject table. The new datum enters as User Input.
function AddFieldRow({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [val, setVal] = useState('');
  if (!open) {
    return (
      <div className="sa-addrow">
        <button type="button" className="sa-addbtn" onClick={() => setOpen(true)}><i className="fi fi-rr-plus-small" /> הוסף נתון</button>
      </div>
    );
  }
  const save = () => { if (name.trim() && val.trim()) { onAdd(name.trim(), val.trim()); setName(''); setVal(''); setOpen(false); } };
  return (
    <div className="sa-addrow is-open">
      <input autoFocus placeholder="שם הנתון" value={name} onChange={(e) => setName(e.target.value)} />
      <input placeholder="ערך" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
      <button type="button" className="np-btn np-btn-primary" onClick={save} disabled={!name.trim() || !val.trim()}>שמירה</button>
      <button type="button" className="np-btn np-btn-ghost" onClick={() => { setOpen(false); setName(''); setVal(''); }}>ביטול</button>
    </div>
  );
}

function SubjectList({ group, onToggle, onAction, onEditValue, onNote, onOpenConflict, onAddField }) {
  const isPlans = group.fields.some((f) => f.kind === 'plan');
  const c = groupCounts(group);
  return (
    <section className={`sa-group ${group.isOpen ? 'is-open' : ''}`}>
      <button type="button" className="sa-group-head" onClick={() => onToggle(group.id)} aria-expanded={group.isOpen}>
        <i className={`fi ${group.isOpen ? 'fi-rr-angle-small-down' : 'fi-rr-angle-small-left'} sa-group-chev`} />
        <span className="sa-group-order">{group.order}</span>
        <span className="sa-group-title">{group.title}</span>
        {/* §7.2: only missing is highlighted (red). Approved / needs-check are
            neutral tallies — no green, no yellow. */}
        <span className="sa-group-counts">
          <span className="sa-cnt">{c.data} נתונים</span>
          {c.approved > 0 && <span className="sa-cnt">{c.approved} אושרו</span>}
          {c.check > 0 && <span className="sa-cnt">{c.check} לבדיקה</span>}
          {c.missing > 0 && <span className="sa-cnt sa-cnt-missing">{c.missing} חסר</span>}
        </span>
      </button>
      {group.isOpen && isPlans && (
        <div className="sa-table" role="table">
          <div className="sa-plan sa-plan-head" role="row">
            <span className="sa-plan-id">מספר ושם תכנית</span>
            <span className="sa-plan-role">סוג ותפקיד ראשוני</span>
            <span className="sa-plan-rel">רלוונטיות</span>
            <span className="sa-plan-status">סטטוס</span>
            <span className="sa-plan-src">מקור</span>
            <span className="sa-plan-act" />
          </div>
          {group.fields.map((f) => (f.kind === 'plan'
            ? <PlanRow key={f.id} field={f} onAction={onAction} onNote={onNote} />
            : <FieldRow key={f.id} field={f} onAction={onAction} onEditValue={onEditValue} onNote={onNote} />))}
        </div>
      )}
      {group.isOpen && !isPlans && (
        <div className="sa-table" role="table">
          <div className="sa-tr sa-tr-head" role="row">
            <span className="sa-th sa-c-name">נתון</span>
            <span className="sa-th sa-c-value">ערך</span>
            <span className="sa-th sa-c-unit">יחידה</span>
            <span className="sa-th sa-c-source">מקור</span>
            <span className="sa-th sa-c-conf">ודאות</span>
            <span className="sa-th sa-c-status">סטטוס</span>
            <span className="sa-th sa-c-act sa-th-act">פעולות</span>
          </div>
          {group.fields.length === 0 && <div className="sa-group-empty">אין נתונים בנושא זה.</div>}
          {group.fields.map((f) => <FieldRow key={f.id} field={f} onAction={onAction} onEditValue={onEditValue} onNote={onNote} onOpenConflict={onOpenConflict} />)}
          {onAddField && <AddFieldRow onAdd={(n, v) => onAddField(group.id, n, v)} />}
        </div>
      )}
    </section>
  );
}

// ── Central gaps aggregation (§12) ───────────────────────────────────────────
function GapRow({ gap, docs, onComplete, onLink, onUpload }) {
  const [val, setVal] = useState('');
  const fileRef = useRef(null);
  return (
    <div className="sa-gap" role="row">
      <span className="sa-gap-name">
        <span className="sa-gap-title">{gap.name}</span>
        <span className="sa-gap-group">{gap.groupTitle}</span>
        {gap.expectedSource && <span className="sa-gap-expected">ייאסף מ: {gap.expectedSource}</span>}
      </span>
      <span className="sa-gap-status">
        <Pill tone={(FIELD_STATUS[gap.status] || FIELD_STATUS.missing).tone}>
          {(FIELD_STATUS[gap.status] || FIELD_STATUS.missing).label}
        </Pill>
      </span>
      <span className="sa-gap-manual">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="השלמה ידנית"
          onKeyDown={(e) => { if (e.key === 'Enter' && val.trim()) { onComplete(gap.id, val.trim()); setVal(''); } }}
        />
        {val.trim() && (
          <button type="button" className="sa-gap-save" onClick={() => { onComplete(gap.id, val.trim()); setVal(''); }}>שמור</button>
        )}
      </span>
      <span className="sa-gap-doc">
        <select defaultValue="" onChange={(e) => { const d = docs[Number(e.target.value)]; if (d) onLink(gap.id, d); e.target.value = ''; }}>
          <option value="" disabled>קשר מסמך מהארכיון…</option>
          {docs.map((d, i) => <option key={i} value={i}>{d.name}</option>)}
        </select>
      </span>
      <span className="sa-gap-upload">
        <button type="button" onClick={() => fileRef.current?.click()}><i className="fi fi-rr-cloud-upload" /> העלה</button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.dwg,.dwf" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onUpload(gap.id, f); }} />
      </span>
    </div>
  );
}

const VM_CACHE = new Map();

export default function StatutoryAnalysis({ project, onBack, onNext, onPrev, onGoStage }) {
  const name = project?.name || 'הפרויקט';
  const [vm, setVm] = useState(() => VM_CACHE.get(project?.id) || null);
  const [loading, setLoading] = useState(() => !VM_CACHE.has(project?.id));
  const [error, setError] = useState('');
  const [loadedProject, setLoadedProject] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmNext, setConfirmNext] = useState(false);
  const [noteFor, setNoteFor] = useState(null); // { fieldId, name, value } | null
  const [resolveFor, setResolveFor] = useState(null); // inline-edit resolution dialog
  const [conflictFor, setConflictFor] = useState(null); // fieldId of an open conflict dialog
  const [dafLoading, setDafLoading] = useState(false); // background daf-meyda fetch in flight
  const editedRef = useRef(false); // any user field edit this session (guards auto-upgrade)

  // Live agent feed (graphic-log §4): what the agent is doing RIGHT NOW — which
  // source is being queried, what came back, what failed — streamed while the
  // fetches run instead of appearing only after the build. Cleared whenever a
  // built snapshot's own log supersedes the interim events.
  const [liveLog, setLiveLog] = useState([]);
  const liveSeq = useRef(0);
  const liveNow = () => new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const pushLive = (text, kind = 'running') => {
    liveSeq.current += 1;
    const id = `lv-${liveSeq.current}`;
    setLiveLog((l) => [...l, { id, time: liveNow(), text, kind }]);
    return id;
  };
  const settleLive = (id, text, kind) => setLiveLog((l) => l.map((e) => (e.id === id ? { ...e, text, kind, time: liveNow() } : e)));

  useEffect(() => { if (vm && project?.id) VM_CACHE.set(project.id, vm); }, [vm, project?.id]);

  useEffect(() => {
    let alive = true;
    // Aborts every heavy fetch this effect starts when the screen unmounts /
    // the user navigates away — so a slow request doesn't linger and hog one of
    // the browser's ~6 connection slots (which was blocking new requests).
    const ctrl = new AbortController();
    if (!project?.id) { setLoading(false); setError('לא נבחר פרויקט.'); return undefined; }
    setError('');
    const cached = VM_CACHE.get(project.id);
    if (cached) { setVm(cached); setLoading(false); } else { setLoading(true); }
    setLiveLog([]);
    const evLoad = pushLive('טוען את נתוני הפרויקט…');
    (async () => {
      try {
        const loaded = await getProject(project.id);
        if (!alive) return;
        settleLive(evLoad, 'נתוני הפרויקט נטענו', 'done');
        // Refresh the applicable plans (with zone rules) from the live KB using the
        // parcel's coordinates — so newly-ingested plans appear even though the
        // identification snapshot (reused by property) never re-runs.
        const idn = loaded.project?.identification || null;
        const coords = idn?.identification?.coordinates || null;
        // Fetch applicable plans + the parcel's GIS land use together — the land
        // use is the key that scopes the zone rules (so a plan's whole zoning table
        // isn't read as conflicting candidates). Reuse a prior land use if cached.
        // Reuse a prior land use only if it already carries the GIS constraint
        // layers (older snapshots predate them) — otherwise refetch so שימור /
        // trees / easements / environmental populate.
        // Applicable plans (our DB) + the GIS land use gate the first render.
        // Both fetches now carry a hard timeout, so a stall can't hang the screen
        // forever (the old bug); if the GIS land use still fails, it's retried in
        // the background below so the scoped fields self-heal without a reopen.
        const luComplete = (lu) => Boolean(lu && lu.conservation && lu.trees && lu.transit);
        const cachedLU = loaded.project?.statutory?.landUse || cached?.landUse || null;
        const priorLandUse = luComplete(cachedLU) ? cachedLU : null;
        const evPlans = pushLive('בודק תכניות חלות מול מאגר התכניות…');
        const evLU = priorLandUse ? null : pushLive('שולף ייעוד ושכבות מגבלות מה-GIS העירוני…');
        const [freshPlans, freshLandUse] = await Promise.all([
          fetchApplicablePlans(coords, ctrl.signal).then((r) => {
            if (alive) settleLive(evPlans, r && r.length ? `נמצאו ${r.length} תכניות חלות` : 'לא התקבלו תכניות מהמאגר', r && r.length ? 'done' : 'warn');
            return r;
          }),
          priorLandUse ? Promise.resolve(priorLandUse) : fetchParcelLandUse(coords, ctrl.signal).then((r) => {
            if (alive) settleLive(evLU, r ? 'ייעוד ושכבות מגבלות נשלפו מה-GIS' : 'שליפת שכבות ה-GIS נכשלה — יבוצע ניסיון חוזר', r ? 'done' : 'warn');
            return r;
          }),
        ]);
        if (!alive) return;
        let zonesAvailable = false;
        if (idn && freshPlans && freshPlans.length) {
          idn.plans = freshPlans;
        }
        for (const p of (idn?.plans || [])) if (p.zones && p.zones.length) zonesAvailable = true;
        setLoadedProject(loaded);
        const want = propWant(propertySignature(loaded.property));
        // The current applicable-plan set (when we could fetch it) — reuse must
        // match it, so a newly-ingested plan forces a rebuild.
        const refsKey = (freshPlans && freshPlans.length)
          ? freshPlans.map((p) => p.number).filter(Boolean).sort().join('|')
          : null;
        const gush = idn?.identification?.gush;
        const helka = idn?.identification?.helka;
        const needsDaf = Boolean(gush && helka);
        // Reuse a snapshot only if it matches the builder version, zone
        // availability, and the plan set (regenerates when any of these change).
        const fresh = (s) => s && s.builtVersion === STAT_VERSION && statPropKey(s.signature) === want
          && s.hasZones === zonesAvailable && (refsKey == null || s.planRefs === refsKey);
        const saved = loaded.project?.statutory;
        // Reuse the prior structured daf meyda only if extracted with the current
        // schema; otherwise refetch (the server cache re-extracts on its own bump).
        const priorDmRaw = (fresh(saved) && saved.dafMeyda) || (fresh(cached) && cached.dafMeyda) || null;
        const priorDm = priorDmRaw && priorDmRaw.schema_v === DM_SCHEMA_V ? priorDmRaw : null;
        loaded.landUse = freshLandUse;
        loaded.landUseTried = true;
        let baseVm;
        if (cached && fresh(cached)) baseVm = cached;
        else if (fresh(saved)) baseVm = saved;
        else {
          loaded.dafMeyda = priorDm;
          loaded.dafMeydaTried = Boolean(priorDm);
          baseVm = buildStatutory(loaded);
          saveStatutory(project.id, baseVm).catch(() => {});
        }
        setVm(baseVm);
        // The built snapshot's log documents the phase we just streamed live —
        // drop the interim events (background daf/GIS events are pushed below).
        setLiveLog([]);

        // Seed `loaded` with whatever the chosen snapshot already carries, so a
        // background re-derive preserves it instead of dropping it.
        loaded.landUse = baseVm.landUse || loaded.landUse || null;
        loaded.dafMeyda = baseVm.dafMeyda || loaded.dafMeyda || null;

        // Background enhancement: fetch the heavy/external sources still missing —
        // the GIS land use and the daf meyda (its round-trip is 20-80s the first
        // time) — in parallel, then re-derive ONCE. Skipped once the user has
        // edited, so we never clobber their choices.
        const hasEdits = (v) => (v?.groups || []).some((g) => (g.fields || []).some((f) => (f.history || []).length));
        const needLU = !luComplete(loaded.landUse);
        // A daf meyda extracted under an older schema counts as missing — a
        // snapshot can be otherwise reusable yet carry stale dm data (built
        // during a deploy window where frontend and backend versions crossed).
        const dmStale = Boolean(loaded.dafMeyda && loaded.dafMeyda.schema_v !== DM_SCHEMA_V);
        const needDm = needsDaf && (!loaded.dafMeyda || dmStale);
        if ((needLU || needDm) && !hasEdits(baseVm)) {
          setDafLoading(true);
          const evDaf = needDm ? pushLive('שולף דף זכויות (מידע מוקדם) מעיריית ת״א — עשוי לקחת עד כדקה…') : null;
          const evLU2 = needLU ? pushLive('משלים שכבות GIS חסרות…') : null;
          Promise.all([
            needLU ? fetchParcelLandUse(coords, ctrl.signal) : Promise.resolve(loaded.landUse),
            needDm ? fetchDafMeyda(gush, helka, ctrl.signal) : Promise.resolve(loaded.dafMeyda),
          ])
            .then(([lu, dm]) => {
              if (!alive) return;
              if (evDaf) settleLive(evDaf, dm ? 'דף הזכויות נשלף — משלב את הנתונים' : 'שליפת דף הזכויות נכשלה', dm ? 'done' : 'warn');
              if (evLU2) settleLive(evLU2, lu ? 'שכבות ה-GIS הושלמו' : 'שליפת שכבות ה-GIS נכשלה', lu ? 'done' : 'warn');
              if (editedRef.current) return;
              if (!lu && !dm) return; // both failed — keep the base render
              loaded.landUse = lu || loaded.landUse;
              loaded.landUseTried = true;
              loaded.dafMeyda = dm || loaded.dafMeyda;
              loaded.dafMeydaTried = true;
              const upgraded = buildStatutory(loaded);
              setVm(upgraded);
              setLiveLog([]); // the rebuilt snapshot's own log now covers these steps
              saveStatutory(project.id, upgraded).catch(() => {});
            })
            .catch(() => {})
            .finally(() => { if (alive) setDafLoading(false); });
        }
      } catch {
        if (alive) setError('טעינת הניתוח הסטטוטורי נכשלה. נסו שוב.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const persist = (next) => { if (project?.id) saveStatutory(project.id, next).catch(() => {}); };

  const mutate = (fn) => setVm((prev) => {
    if (!prev) return prev;
    editedRef.current = true; // guard: don't let a late daf-meyda upgrade clobber edits
    const next = { ...prev, groups: fn(prev.groups) };
    persist(next);
    return next;
  });

  const onToggle = (id) => setVm((prev) => (prev ? { ...prev, groups: toggleGroupOpen(prev.groups, id) } : prev));
  const openAll = (open) => setVm((prev) => (prev ? { ...prev, groups: setAllOpen(prev.groups, open) } : prev));

  // §2 — the user can ADD a datum the agent didn't derive. It enters as User
  // Input (manual value, no supporting source) with a full audit trail.
  const onAddField = (groupId, name, value) => setVm((prev) => {
    if (!prev) return prev;
    const at = new Date().toISOString();
    const field = {
      id: `${groupId}-user-${at.replace(/\D/g, '').slice(-10)}`,
      fieldName: null, groupId, name, value: String(value), unit: '',
      extra: null, source: { label: 'הזנה ידנית', link: null },
      database: null, dataset: null, sourceRef: null,
      confidence: 'user_defined', status: 'user_input',
      missingKind: 'missing', expectedSource: null,
      candidates: null, conflict: false, systemRecommendation: null, userResolution: null,
      originalValue: null, note: '', inScope: true, userAdded: true,
      history: [{ at, action: 'user_add', from: null, to: String(value) }],
      lastUpdated: at,
    };
    const next = { ...prev, groups: prev.groups.map((g) => (g.id === groupId ? { ...g, fields: [...g.fields, field] } : g)) };
    persist(next);
    return next;
  });

  // Field actions (§11).
  // Audit-trail wrapper (spec §2 "כלל פיתוח" + §14): every user action appends a
  // history entry — old value, new value, action, timestamp — and stamps
  // lastUpdated. Nothing is ever deleted from the history.
  const withAudit = (f, patch, action, note) => {
    const at = new Date().toISOString();
    return {
      ...patch,
      lastUpdated: at,
      history: [...(f.history || []), {
        at, action,
        from: f.value ?? null,
        to: Object.prototype.hasOwnProperty.call(patch, 'value') ? (patch.value ?? null) : (f.value ?? null),
        ...(note ? { note } : {}),
      }],
    };
  };
  const isGap = (s) => s === 'missing' || s === 'missing_source' || s === 'not_found';

  const onAction = (fieldId, action, payload) => mutate((groups) => {
    if (action === 'approve') return updateField(groups, fieldId, (f) => withAudit(f, { status: 'approved' }, 'approve'));
    // Correction of a sourced value (the source stays; the original is kept).
    if (action === 'edit') return updateField(groups, fieldId, (f) => withAudit(f, {
      originalValue: f.originalValue ?? f.value, value: payload,
      status: isGap(f.status) ? 'user_input' : f.status,
      ...(isGap(f.status) ? { confidence: 'user_defined' } : {}),
    }, 'edit'));
    // Override (§14): user replaces a sourced value with their own — the source
    // markers are cleared (the value no longer comes from it), original kept.
    if (action === 'override') return updateField(groups, fieldId, (f) => withAudit(f, {
      originalValue: f.originalValue ?? f.value, value: payload, status: 'override',
      source: { label: 'ערך משתמש', link: null },
      database: null, dataset: null, sourceRef: null,
    }, 'override'));
    if (action === 'note') return updateField(groups, fieldId, (f) => withAudit(f, { note: payload }, 'note'));
    if (action === 'linkdoc') return updateField(groups, fieldId, (f) => withAudit(f, {
      source: { label: payload.name || 'מסמך', link: payload.link || null },
      database: { id: 'db_project_archive', name: 'ארכיון מסמכי הפרויקט' },
      dataset: { id: `ds_doc_${payload.name || 'linked'}`, name: payload.name || 'מסמך מקושר', type: 'uploaded_document' },
      sourceRef: { link: payload.link || null, page: null, section: null },
      confidence: f.confidence === 'missing' ? 'medium' : f.confidence,
      status: isGap(f.status) ? 'check' : f.status,
    }, 'link_source', payload.name));
    // §17 — resolve a conflict by picking a candidate. Keep `conflict` true so the
    // chooser stays reachable; the pick is recorded as the user_resolution.
    if (action === 'pickcandidate') return updateField(groups, fieldId, (f) => withAudit(f, {
      value: payload.value, unit: payload.unit || f.unit,
      source: payload.source ? { label: payload.source.label, link: payload.source.link || null } : f.source,
      database: payload.source?.database || f.database || null,
      dataset: payload.source?.dataset || f.dataset || null,
      sourceRef: payload.source ? { link: payload.source.link || null, page: payload.source.page ?? null, section: payload.source.section ?? null } : f.sourceRef,
      confidence: payload.confidence || f.confidence, status: 'check',
      userResolution: { value: payload.value, at: new Date().toISOString() },
    }, 'resolve_conflict'));
    // Fill an empty field inline — User Input (§14): a manual value with no
    // supporting source, clearly marked, lower certainty.
    if (action === 'complete-inline') return updateField(groups, fieldId, (f) => withAudit(f, {
      value: payload, source: { label: 'הזנה ידנית', link: null },
      database: null, dataset: null, sourceRef: null,
      confidence: 'user_defined', status: 'user_input',
    }, 'user_input'));
    if (action === 'scope') return updateField(groups, fieldId, (f) => withAudit(f, { inScope: false, status: 'not_in_scope' }, 'remove_from_scope'));
    if (action === 'restore') return updateField(groups, fieldId, (f) => withAudit(f, { inScope: true, status: f.value ? 'check' : (f.missingKind || 'missing') }, 'restore_to_scope'));
    // Clear a value back to a gap (undo a manual completion / discard a value) —
    // returning to the field's original gap flavor (§14).
    if (action === 'clear') return updateField(groups, fieldId, (f) => withAudit(f, {
      value: null, source: null, database: null, dataset: null, sourceRef: null,
      extra: null, confidence: 'missing', status: f.missingKind || 'missing',
      originalValue: null, inScope: true,
    }, 'clear'));
    return groups;
  });

  // Inline value edit: fill a blank directly; for a real change, ask how to keep
  // it (correct vs override); emptying an existing value clears it back to a gap.
  const editValue = (field, raw) => {
    const v = (raw || '').trim();
    const old = field.value || '';
    if (v === old) return;
    if (!old && v) { onAction(field.id, 'complete-inline', v); return; }
    if (old && !v) { setResolveFor({ mode: 'clear', field }); return; }
    setResolveFor({ mode: 'update', field, newValue: v });
  };

  // Gap completions (§12/§14): manual value = User Input. Linking/uploading a
  // document attaches it as the field's source Dataset but does NOT invent a
  // value (the doc's filename is not the datum) — the field moves to
  // "לא נמצא במקור" until the user reads the value out of the doc and types it.
  const completeGap = (fieldId, manualValue) => onAction(fieldId, 'complete-inline', manualValue);
  const archiveDb = { id: 'db_project_archive', name: 'ארכיון מסמכי הפרויקט' };
  const attachGapSource = (fieldId, name, link, uploaded) => mutate((groups) => updateField(groups, fieldId, (f) => ({
    source: { label: name || 'מסמך', link: link || null, ...(uploaded ? { uploaded: true, at: new Date().toISOString() } : {}) },
    database: archiveDb,
    dataset: { id: `ds_doc_${name || 'linked'}`, name: name || 'מסמך מקושר', type: 'uploaded_document' },
    sourceRef: { link: link || null, page: null, section: null },
    status: f.value ? f.status : 'not_found',
    note: f.value ? f.note : `מקור מקושר: ${name || 'מסמך'} — יש לאתר את הערך במסמך ולהזינו`,
    lastUpdated: new Date().toISOString(),
    history: [...(f.history || []), { at: new Date().toISOString(), action: uploaded ? 'upload_source' : 'link_source', from: f.value ?? null, to: f.value ?? null, note: name }],
  })));
  const linkGapDoc = (fieldId, doc) => attachGapSource(fieldId, doc.name, doc.link, false);
  const uploadGapDoc = async (fieldId, file) => {
    let link = null;
    try { link = await uploadFile(file); } catch { /* metadata only */ }
    // `uploaded` marks it for the document archive (buildArchive reads these).
    attachGapSource(fieldId, file.name, link, true);
  };

  const handleSaveDraft = async () => {
    if (!project?.id) { onBack?.(); return; }
    setSavingDraft(true);
    try { await updateProject(project.id, { status: 'draft' }); onBack?.(); }
    catch { setError('שמירת הטיוטה נכשלה. נסו שוב.'); setSavingDraft(false); }
  };

  const gaps = collectGaps(vm?.groups || []);
  const allOpen = (vm?.groups || []).length > 0 && vm.groups.every((g) => g.isOpen);
  // Documents available to link from the archive (uploaded + manual gap files).
  const archiveDocs = (loadedProject?.documents || [])
    .map((d) => ({ name: d.file_name || d.document_type || 'מסמך', link: (d.file_id && /^https?:\/\//.test(d.file_id)) ? d.file_id : null }));

  const goNext = () => { if (gaps.length) setConfirmNext(true); else onNext?.(project); };

  // The rail shows the snapshot's log plus any live in-flight events (§4).
  const log = [...(vm?.log || []), ...liveLog];

  return (
    <div className="sa">
      <nav className="np-crumbs">
        <button type="button" onClick={onBack}>פרויקטים</button>
        <i className="fi fi-rr-angle-small-left" />
        <span className="sa-crumb-name">{name}</span>
        <i className="fi fi-rr-angle-small-left" />
        <span>ניתוח מצב סטטוטורי</span>
      </nav>

      <header className="pid-workhead">
        <div className="pid-workhead-text">
          <h1>{name}</h1>
          <p>סקירה, אישור והשלמה של הנתונים שחולצו מהמקורות — שכבת הדאטה שתשמש את השלבים הבאים.</p>
        </div>
        <div className="pid-workhead-actions">
          <button type="button" className="np-btn np-btn-secondary" onClick={() => onPrev?.(project)} disabled={loading}>
            <i className="fi fi-rr-angle-small-right" /> הקודם
          </button>
          <button type="button" className="np-btn np-btn-secondary" onClick={handleSaveDraft} disabled={loading || savingDraft}>
            <i className="fi fi-rr-bookmark" /> {savingDraft ? 'שומר…' : 'שמירת טיוטה'}
          </button>
          <button type="button" className="np-btn np-btn-primary" onClick={goNext} disabled={loading || !vm}>
            הבא <i className="fi fi-rr-angle-small-left" />
          </button>
        </div>
      </header>

      {error && <div className="pid-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

      <div className="pid-layout">
        <aside className="pid-rail">
          <AgentActivity log={log} loading={loading} />
          <Roadmap
            activeStage={ACTIVE_STAGE}
            reachedStage={Math.max(ACTIVE_STAGE, reachedStageFor(loadedProject?.project))}
            onStageClick={(i) => onGoStage?.(i, { loaded: loadedProject, project })}
          />
          <DocumentArchive inline projectId={project?.id} />
        </aside>

        <div className="pid-work">
          <div className="sa-collected">
            <i className="fi fi-rr-info" />
            <span>המערכת אספה את הנתונים מתוך המקורות שנאספו וסידרה אותם לפי נושאים. אשרו, ערכו או השלימו כל נתון.</span>
          </div>

          <div className="sa-toolbar">
            <h2 className="sa-lists-title">נתונים לפי נושאים</h2>
            <div className="sa-toolbar-actions">
              {dafLoading && (
                <span className="sa-daf-loading"><span className="rp-spinner" /> משלים נתונים ממקורות העירייה…</span>
              )}
              <button type="button" className="sa-toggle" onClick={() => openAll(!allOpen)}>
                <i className={`fi ${allOpen ? 'fi-rr-square' : 'fi-rr-list'}`} /> {allOpen ? 'סגור הכל' : 'פתח הכל'}
              </button>
            </div>
          </div>

          {loading && !vm ? (
            <div className="sa-loading"><span className="rp-spinner" /> מארגן את הנתונים לפי נושאים…</div>
          ) : (
            <div className="sa-groups">
              {(vm?.groups || []).map((g) => (
                <SubjectList
                  key={g.id}
                  group={g}
                  onToggle={onToggle}
                  onAction={onAction}
                  onEditValue={editValue}
                  onNote={(f) => setNoteFor({ fieldId: f.id, name: f.name, value: f.note || '' })}
                  onOpenConflict={(f) => setConflictFor(f.id)}
                  onAddField={onAddField}
                />
              ))}
            </div>
          )}

          {gaps.length > 0 && (
            <>
              <div className="sa-gaps-notice">
                <i className="fi fi-rr-exclamation" />
                <span>אלו הנתונים שהמערכת לא הצליחה לאסוף וניתן להשלים אותם באופן ידני.</span>
              </div>
              <section className="pid-card sa-gaps">
                <header className="pid-card-head">
                  <i className="fi fi-rr-interrogation" />
                  <h3>ריכוז חוסרים</h3>
                  <span className="pid-count">{gaps.length}</span>
                </header>
                <div className="sa-gaps-table">
                  <div className="sa-gap sa-gap-head" role="row">
                    <span className="sa-gap-name">נתון חסר</span>
                    <span className="sa-gap-status">סטטוס</span>
                    <span className="sa-gap-manual">השלמה ידנית</span>
                    <span className="sa-gap-doc">מסמך מארכיון</span>
                    <span className="sa-gap-upload">העלאה</span>
                  </div>
                  {(() => {
                    // Gaps are grouped by the SOURCE the system couldn't reach
                    // (graphic-log §2.3): "לא ניתן לגשת לתיק הבניין" leads, and
                    // the individual fields it would provide sit under it —
                    // never as free-standing gaps. Field-level gaps (the source
                    // was reachable but the datum wasn't there) come after.
                    const srcGroups = [];
                    const fieldGaps = [];
                    for (const g of gaps) {
                      if (g.status === 'missing_source' || g.missingKind === 'missing_source') {
                        const name = canonicalSourceName(g.expectedSource);
                        let grp = srcGroups.find((x) => x.name === name);
                        if (!grp) { grp = { name, items: [] }; srcGroups.push(grp); }
                        grp.items.push(g);
                      } else fieldGaps.push(g);
                    }
                    return (
                      <>
                        {srcGroups.map((sg) => (
                          <React.Fragment key={sg.name}>
                            <div className="sa-gap-srchead" role="row">
                              <i className="fi fi-rr-exclamation" />
                              <span>לא ניתן לגשת ל: <strong>{sg.name}</strong></span>
                              <span className="sa-gap-srccount">{sg.items.length === 1 ? 'נתון אחד' : `${sg.items.length} נתונים`}</span>
                            </div>
                            {sg.items.map((g) => (
                              <GapRow key={g.id} gap={g} docs={archiveDocs} onComplete={completeGap} onLink={linkGapDoc} onUpload={uploadGapDoc} />
                            ))}
                          </React.Fragment>
                        ))}
                        {fieldGaps.length > 0 && (
                          <>
                            <div className="sa-gap-srchead is-fields" role="row">
                              <i className="fi fi-rr-interrogation" />
                              <span>נתונים שלא אותרו במקורות שנאספו</span>
                              <span className="sa-gap-srccount">{fieldGaps.length === 1 ? 'נתון אחד' : `${fieldGaps.length} נתונים`}</span>
                            </div>
                            {fieldGaps.map((g) => (
                              <GapRow key={g.id} gap={g} docs={archiveDocs} onComplete={completeGap} onLink={linkGapDoc} onUpload={uploadGapDoc} />
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {noteFor && (
        <NoteDialog
          name={noteFor.name}
          value={noteFor.value}
          onSave={(text) => { onAction(noteFor.fieldId, 'note', text); setNoteFor(null); }}
          onClose={() => setNoteFor(null)}
        />
      )}

      {(() => {
        if (!conflictFor) return null;
        const cf = (vm?.groups || []).flatMap((g) => g.fields).find((f) => f.id === conflictFor);
        if (!cf) return null;
        return (
          <ConflictDialog
            field={cf}
            docs={archiveDocs}
            onPick={(c) => onAction(cf.id, 'pickcandidate', c)}
            onLinkDoc={(d) => onAction(cf.id, 'linkdoc', d)}
            onManual={(v) => {
              // Spec §3 replacement flow: a manual value on a sourced field is an
              // Override; on an empty/unsourced field it's User Input.
              if (cf.value && cf.source) onAction(cf.id, 'override', v);
              else onAction(cf.id, 'complete-inline', v);
            }}
            onClose={() => setConflictFor(null)}
          />
        );
      })()}

      {/* Inline-edit resolution: how to keep a changed value, or confirm a clear. */}
      {resolveFor?.mode === 'update' && (
        <ConfirmDialog
          title="עדכון הערך"
          message={`שיניתם את «${resolveFor.field.name}» מ־«${resolveFor.field.value}» ל־«${resolveFor.newValue}». כיצד לשמור?`}
          onDismiss={() => setResolveFor(null)}
          actions={[
            { label: 'תיקון (שמירת המקור)', tone: 'ghost', onClick: () => { onAction(resolveFor.field.id, 'edit', resolveFor.newValue); setResolveFor(null); } },
            { label: 'החלפת ערך המקור', tone: 'primary', onClick: () => { onAction(resolveFor.field.id, 'override', resolveFor.newValue); setResolveFor(null); } },
          ]}
        />
      )}
      {resolveFor?.mode === 'clear' && (
        <ConfirmDialog
          title="ניקוי הערך"
          message={`הערך של «${resolveFor.field.name}» יימחק והנתון יחזור לרשימת החוסרים.`}
          onDismiss={() => setResolveFor(null)}
          actions={[
            { label: 'ביטול', tone: 'ghost', onClick: () => setResolveFor(null) },
            { label: 'נקה ערך', tone: 'danger', onClick: () => { onAction(resolveFor.field.id, 'clear'); setResolveFor(null); } },
          ]}
        />
      )}

      {confirmNext && (
        <ConfirmDialog
          title="להמשיך עם חוסרים?"
          message={`נותרו ${gaps.length} נתונים חסרים. אפשר להמשיך לשלב הבא ולהשלים אותם מאוחר יותר.`}
          onDismiss={() => setConfirmNext(false)}
          actions={[
            { label: 'המשך השלמה', tone: 'ghost', onClick: () => setConfirmNext(false) },
            { label: 'המשך לשלב הבא', tone: 'primary', onClick: () => { setConfirmNext(false); onNext?.(project); } },
          ]}
        />
      )}
    </div>
  );
}
